import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faFileLines,
  faFilePdf,
  faFileWord,
  faPenNib,
  faRotateRight,
  faWandSparkles
} from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import html2pdf from 'html2pdf.js';
import { saveAs } from 'file-saver';
import { collection, getDocs } from 'firebase/firestore';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import { db } from '../../firebase';
import logo from '../../Images/logo-main-black.png';
import signature from '../../Images/signature.png';
import './DocumentGenerator.css';

const DOCUMENT_TYPES = [
  { value: 'recommendation', label: 'Recommendation Letter' },
  { value: 'confirmation', label: 'Student Confirmation Letter' },
  { value: 'attachment', label: 'Attachment / Internship Letter' },
  { value: 'boardResolution', label: 'Board Resolution Letter' },
  { value: 'minutes', label: 'Minutes Letter' },
  { value: 'custom', label: 'Custom Business Letter' }
];

const STUDENT_DOCUMENT_TYPES = new Set(['recommendation', 'confirmation', 'attachment']);
const BOARD_DOCUMENT_TYPE = 'boardResolution';
const MINUTES_DOCUMENT_TYPE = 'minutes';
const CUSTOM_DOCUMENT_TYPE = 'custom';

const TONE_OPTIONS = [
  { value: 'formal', label: 'Formal' },
  { value: 'warm', label: 'Warm' },
  { value: 'concise', label: 'Concise' }
];

const DOCUMENT_GENERATION_API_BASE = (
  process.env.REACT_APP_DOCUMENT_GENERATION_API_BASE
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/documentGenerationApi'
);

const DOCUMENT_GENERATION_SHARED_SECRET = process.env.REACT_APP_DOCUMENT_GENERATION_SHARED_SECRET || '';

const createDefaultFormState = () => ({
  documentType: 'recommendation',
  tone: 'formal',
  studentName: 'Jane Wanjiku',
  admissionNumber: 'UBU/2024/0142',
  course: 'Diploma in Software Engineering',
  cohort: 'Nairobi Cohort',
  recipientName: 'Admissions Officer',
  recipientTitle: 'Institution / Employer',
  requestedOutcome: 'admission, internship, or employment consideration',
  relationshipContext: 'I have supervised the student during academic and practical sessions.',
  strengthOne: 'strong academic discipline',
  strengthTwo: 'excellent communication',
  strengthThree: 'a reliable and collaborative work ethic',
  extraNotes: 'The student is punctual, respectful, and consistent in meeting deadlines.',
  signatoryName: 'Reinhard Omukuba',
  signatoryRole: 'Director',
  meetingTitle: 'Board Meeting',
  meetingDate: formatDisplayDate(new Date()),
  meetingLocation: 'Ubunifu College Board Room',
  meetingAttendees: 'Chairperson, Secretary, Members',
  boardResolutionSummary: 'The board reviewed the matter and approved the resolution stated in the document.',
  boardResolutionPoints: '1. Opening of the meeting\n2. Review of the agenda\n3. Resolution adopted',
  minutesSummary: 'The meeting considered the listed agenda items and recorded the final decisions below.',
  minutesItems: '1. Attendance was recorded\n2. The agenda was adopted\n3. Resolutions were noted',
  businessSubject: 'Business Correspondence',
  businessSummary: 'This letter communicates the business matter described below.',
  includeLetterhead: true,
  includeReference: true
});

const pad = (value) => String(value).padStart(2, '0');

const buildReferenceNumber = (date = new Date()) => {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
  const randomSuffix = Math.random().toString(36).slice(2, 6).toUpperCase();

  return `UBU-LET-${year}${month}${day}-${hours}${minutes}${seconds}-${milliseconds}-${randomSuffix}`;
};

const formatDisplayDate = (date = new Date()) => {
  return new Intl.DateTimeFormat('en-KE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(date);
};

const normalizeText = (value) => String(value || '').trim();

const sanitizeFileName = (value) => {
  const safeValue = normalizeText(value)
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '');

  return safeValue || 'document';
};

const escapeHtml = (value) => (
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
);

const splitParagraphs = (value) => {
  return normalizeText(value)
    .split(/\n\s*\n/g)
    .map((part) => part.trim())
    .filter(Boolean);
};

const loadImageAsDataUrl = async (imageUrl) => {
  const response = await fetch(imageUrl);
  if (!response.ok) {
    throw new Error('Unable to load image asset');
  }

  const blob = await response.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const collegeContactLines = [
  'Ubunifu College',
  'Nairobi Kenya',
  'Tel: 0727634101',
  'info@ubunifucollege.com',
  'www.ubunifucollege.com'
];

function DocumentGenerator() {
  const previewRef = useRef(null);

  const [form, setForm] = useState(createDefaultFormState);
  const [referenceNumber, setReferenceNumber] = useState(() => buildReferenceNumber());
  const [exporting, setExporting] = useState('');
  const [generatingDocument, setGeneratingDocument] = useState(false);
  const [savingDocument, setSavingDocument] = useState(false);
  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const [contentModalOpen, setContentModalOpen] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [promptDraft, setPromptDraft] = useState('');
  const [promptHistory, setPromptHistory] = useState([]);
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [savedDocumentId, setSavedDocumentId] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [studentDirectory, setStudentDirectory] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');

  const activeType = DOCUMENT_TYPES.find((item) => item.value === form.documentType) || DOCUMENT_TYPES[0];
  const isStudentDocument = STUDENT_DOCUMENT_TYPES.has(form.documentType);
  const isBoardResolutionDocument = form.documentType === BOARD_DOCUMENT_TYPE;
  const isMinutesDocument = form.documentType === MINUTES_DOCUMENT_TYPE;
  const isCustomDocument = form.documentType === CUSTOM_DOCUMENT_TYPE;
  const generatedDate = useMemo(() => formatDisplayDate(new Date()), []);

  const localPreviewData = useMemo(() => {
    const studentName = normalizeText(form.studentName) || 'Student Name';
    const admissionNumber = normalizeText(form.admissionNumber) || 'Admission Number';
    const course = normalizeText(form.course) || 'Course Name';
    const cohort = normalizeText(form.cohort);
    const recipientName = normalizeText(form.recipientName) || 'Sir / Madam';
    const recipientTitle = normalizeText(form.recipientTitle) || 'Recipient';
    const requestedOutcome = normalizeText(form.requestedOutcome) || 'the opportunity requested';
    const relationshipContext = normalizeText(form.relationshipContext) || 'I have worked closely with the student.';
    const strengthLines = [form.strengthOne, form.strengthTwo, form.strengthThree]
      .map(normalizeText)
      .filter(Boolean);
    const extraNotes = splitParagraphs(form.extraNotes);
    const meetingTitle = normalizeText(form.meetingTitle) || 'Board Meeting';
    const meetingDate = normalizeText(form.meetingDate) || generatedDate;
    const meetingLocation = normalizeText(form.meetingLocation) || 'Ubunifu College Board Room';
    const meetingAttendees = normalizeText(form.meetingAttendees) || 'Chairperson, Secretary, Members';
    const boardResolutionSummary = normalizeText(form.boardResolutionSummary) || 'The board reviewed the matter and approved the resolution stated in the document.';
    const boardResolutionPoints = splitParagraphs(form.boardResolutionPoints);
    const minutesSummary = normalizeText(form.minutesSummary) || 'The meeting considered the listed agenda items and recorded the final decisions below.';
    const minutesItems = splitParagraphs(form.minutesItems);
    const businessSubject = normalizeText(form.businessSubject) || 'Business Correspondence';
    const businessSummary = normalizeText(form.businessSummary) || 'This letter communicates the business matter described below.';
    const studentDescriptor = cohort ? `${course} (${cohort})` : course;
    const studentSubjectMap = {
      recommendation: 'Recommendation Letter',
      confirmation: 'Student Confirmation Letter',
      attachment: 'Attachment / Internship Letter'
    };

    let openingParagraphs = [];
    let bodyParagraphs = [];
    let bulletHeading = '';
    let bulletItems = [];
    let closingLabel = 'Yours faithfully,';
    let subject = activeType.label;

    if (isStudentDocument) {
      openingParagraphs = [
        `Dear ${recipientName},`,
        `Re: ${activeType.label} for ${studentName}`
      ];
      bodyParagraphs = [
        `I am pleased to recommend ${studentName}, admission number ${admissionNumber}, for ${requestedOutcome}.`,
        `The student is enrolled in ${studentDescriptor}. ${relationshipContext}`,
        strengthLines.length
          ? `${studentName} has consistently demonstrated ${strengthLines.join(', ')}.`
          : `${studentName} has demonstrated the qualities expected of a strong candidate.`,
        ...extraNotes,
        `Based on the above, I confidently support ${studentName} for ${requestedOutcome}. Please feel free to contact me should you need any additional information.`
      ];
      bulletHeading = 'Key strengths';
      bulletItems = strengthLines;
      subject = studentSubjectMap[form.documentType] || activeType.label;
    } else if (isBoardResolutionDocument) {
      openingParagraphs = [
        `Meeting title: ${meetingTitle}.`,
        `Meeting date: ${meetingDate}.`,
        `Venue: ${meetingLocation}.`,
        `Attendance: ${meetingAttendees}.`
      ];
      bodyParagraphs = [
        boardResolutionSummary,
        ...extraNotes
      ];
      bulletHeading = 'Resolution points';
      bulletItems = boardResolutionPoints;
      closingLabel = 'Certified by,';
      subject = 'Board Resolution Letter';
    } else if (isMinutesDocument) {
      openingParagraphs = [
        `Meeting title: ${meetingTitle}.`,
        `Meeting date: ${meetingDate}.`,
        `Venue: ${meetingLocation}.`,
        `Attendance: ${meetingAttendees}.`
      ];
      bodyParagraphs = [
        minutesSummary,
        ...extraNotes
      ];
      bulletHeading = 'Minutes items';
      bulletItems = minutesItems;
      closingLabel = 'Recorded by,';
      subject = 'Minutes Letter';
    } else {
      openingParagraphs = [
        `Dear ${recipientName},`,
        `Subject: ${businessSubject}.`
      ];
      bodyParagraphs = [
        businessSummary,
        `This correspondence concerns ${requestedOutcome}.`,
        relationshipContext,
        ...extraNotes
      ].filter((paragraph) => normalizeText(paragraph));
      subject = 'Custom Business Letter';
    }

    return {
      studentName,
      admissionNumber,
      course,
      cohort,
      recipientName,
      recipientTitle,
      requestedOutcome,
      relationshipContext,
      strengthLines,
      openingParagraphs,
      bodyParagraphs,
      bulletHeading,
      bulletItems,
      closingLabel,
      subject,
      documentSubject: subject,
      noteCount: extraNotes.length
    };
  }, [form, activeType.label]);

  const previewData = useMemo(() => {
    if (!generatedDocument) {
      return localPreviewData;
    }

    const fallbackSubject = generatedDocument.subject || localPreviewData.subject;

    return {
      ...localPreviewData,
      ...generatedDocument,
      subject: fallbackSubject,
      documentSubject: generatedDocument.subject || localPreviewData.documentSubject,
      openingParagraphs: Array.isArray(generatedDocument.openingParagraphs) && generatedDocument.openingParagraphs.length
        ? generatedDocument.openingParagraphs
        : localPreviewData.openingParagraphs,
      bodyParagraphs: Array.isArray(generatedDocument.bodyParagraphs) && generatedDocument.bodyParagraphs.length
        ? generatedDocument.bodyParagraphs
        : localPreviewData.bodyParagraphs,
      bulletHeading: generatedDocument.bulletHeading || localPreviewData.bulletHeading,
      bulletItems: Array.isArray(generatedDocument.bulletItems) && generatedDocument.bulletItems.length
        ? generatedDocument.bulletItems
        : localPreviewData.bulletItems,
      closingLabel: generatedDocument.closingLabel || localPreviewData.closingLabel,
      signatoryName: generatedDocument.signatoryName || form.signatoryName,
      signatoryRole: generatedDocument.signatoryRole || form.signatoryRole,
      noteCount: localPreviewData.noteCount
    };
  }, [form.signatoryName, form.signatoryRole, generatedDocument, localPreviewData]);

  const documentIsSaved = Boolean(savedDocumentId);

  const promptText = useMemo(() => {
    const sections = [`Write a polished ${activeType.label.toLowerCase()} for a school or college administrator.`];

    if (isStudentDocument) {
      sections.push(
        `Student name: ${previewData.studentName}`,
        `Admission number: ${previewData.admissionNumber}`,
        `Course: ${previewData.course}${previewData.cohort ? ` (${previewData.cohort})` : ''}`,
        `Recipient: ${previewData.recipientName} - ${previewData.recipientTitle}`,
        `Requested outcome: ${previewData.requestedOutcome}`,
        `Context: ${previewData.relationshipContext}`,
        `Strengths: ${previewData.strengthLines.length ? previewData.strengthLines.join(', ') : 'Not specified'}`
      );
    } else if (isBoardResolutionDocument) {
      sections.push(
        `Meeting title: ${normalizeText(form.meetingTitle) || 'Board Meeting'}`,
        `Meeting date: ${normalizeText(form.meetingDate) || generatedDate}`,
        `Meeting location: ${normalizeText(form.meetingLocation) || 'Ubunifu College Board Room'}`,
        `Attendance: ${normalizeText(form.meetingAttendees) || 'Chairperson, Secretary, Members'}`,
        `Resolution summary: ${normalizeText(form.boardResolutionSummary) || 'The board reviewed the matter and approved the resolution stated in the document.'}`,
        `Resolution points: ${normalizeText(form.boardResolutionPoints) || 'Not specified'}`
      );
    } else if (isMinutesDocument) {
      sections.push(
        `Meeting title: ${normalizeText(form.meetingTitle) || 'Board Meeting'}`,
        `Meeting date: ${normalizeText(form.meetingDate) || generatedDate}`,
        `Meeting location: ${normalizeText(form.meetingLocation) || 'Ubunifu College Board Room'}`,
        `Attendance: ${normalizeText(form.meetingAttendees) || 'Chairperson, Secretary, Members'}`,
        `Minutes summary: ${normalizeText(form.minutesSummary) || 'The meeting considered the listed agenda items and recorded the final decisions below.'}`,
        `Minutes items: ${normalizeText(form.minutesItems) || 'Not specified'}`
      );
    } else {
      sections.push(
        `Recipient: ${previewData.recipientName} - ${previewData.recipientTitle}`,
        `Business subject: ${normalizeText(form.businessSubject) || 'Business Correspondence'}`,
        `Business summary: ${normalizeText(form.businessSummary) || 'This letter communicates the business matter described below.'}`,
        `Requested outcome: ${previewData.requestedOutcome}`,
        `Context: ${previewData.relationshipContext}`
      );
    }

    sections.push(
      `Tone: ${normalizeText(form.tone) || 'formal'}`,
      `Optional sections enabled - letterhead: ${form.includeLetterhead ? 'yes' : 'no'}, reference number: ${form.includeReference ? 'yes' : 'no'}`,
      'Return a final letter that is ready to review, edit, and export as a Word document or PDF.'
    );

    return sections.join('\n');
  }, [activeType.label, form, generatedDate, isBoardResolutionDocument, isMinutesDocument, isStudentDocument, previewData]);

  useEffect(() => {
    if (promptModalOpen && !promptDraft) {
      setPromptDraft(promptText);
    }
  }, [promptModalOpen, promptText, promptDraft]);

  useEffect(() => {
    let active = true;

    const loadStudentDirectory = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'users'));
        if (!active) return;

        const students = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          const displayName = normalizeText(data.bio || data.fullName || data.name || data.studentName || data.displayName || 'Student');
          const admissionNumber = normalizeText(data.admissionNumber || data.admissionNo || data.studentNumber || data.userId || doc.id);
          const course = normalizeText(data.course || data.program || data.programme || data.courseName);
          const cohort = normalizeText(data.cohort || data.className || data.intake || data.campus);
          const status = normalizeText(data.userStatus || data.status);
          const searchBlob = [
            displayName,
            admissionNumber,
            course,
            cohort,
            status
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          if (!displayName || (!admissionNumber && !course)) {
            return;
          }

          students.push({
            id: doc.id,
            studentKey: normalizeText(data.userId || doc.id),
            displayName,
            admissionNumber,
            course,
            cohort,
            status,
            searchBlob
          });
        });

        students.sort((left, right) => left.displayName.localeCompare(right.displayName));
        setStudentDirectory(students);
      } catch (error) {
        console.error('Failed to load student directory:', error);
      }
    };

    loadStudentDirectory();

    return () => {
      active = false;
    };
  }, []);

  const selectedStudent = useMemo(() => (
    studentDirectory.find((student) => student.studentKey === selectedStudentId) || null
  ), [selectedStudentId, studentDirectory]);

  const studentMatches = useMemo(() => {
    const searchTerm = normalizeText(studentSearchTerm).toLowerCase();
    const students = studentDirectory
      .map((student) => {
        const byName = student.displayName.toLowerCase().includes(searchTerm);
        const byAdmission = student.admissionNumber.toLowerCase().includes(searchTerm);
        const byCourse = student.course.toLowerCase().includes(searchTerm);
        const byCohort = student.cohort.toLowerCase().includes(searchTerm);
        const score = searchTerm
          ? (student.displayName.toLowerCase().startsWith(searchTerm) ? 80 : 0)
            + (byName ? 40 : 0)
            + (byAdmission ? 30 : 0)
            + (byCourse ? 20 : 0)
            + (byCohort ? 10 : 0)
          : 0;

        return {
          ...student,
          score
        };
      })
      .filter((student) => {
        if (!searchTerm) return false;
        return student.searchBlob.includes(searchTerm);
      })
      .sort((left, right) => right.score - left.score || left.displayName.localeCompare(right.displayName));

    return students.slice(0, 8);
  }, [studentDirectory, studentSearchTerm]);

  const updateField = (field) => (event) => {
    const { value, type, checked } = event.target;
    if (['studentName', 'admissionNumber', 'course', 'documentType'].includes(field)) {
      setSelectedStudentId('');
      if (field === 'documentType') {
        setStudentSearchTerm('');
      }
    }
    setGeneratedDocument(null);
    setSavedDocumentId('');
    setGenerationError('');
    setSaveError('');
    setForm((current) => ({
      ...current,
      [field]: type === 'checkbox' ? checked : value
    }));
  };

  const handleStudentSearchChange = (event) => {
    setStudentSearchTerm(event.target.value);
    setSelectedStudentId('');
  };

  const selectStudent = (student) => {
    setSelectedStudentId(student.studentKey);
    setStudentSearchTerm(student.displayName);
    setGeneratedDocument(null);
    setSavedDocumentId('');
    setGenerationError('');
    setSaveError('');
    setForm((current) => ({
      ...current,
      studentName: student.displayName,
      admissionNumber: student.admissionNumber || current.admissionNumber,
      course: student.course || current.course,
      cohort: student.cohort || current.cohort
    }));
  };

  const clearStudentSelection = () => {
    setSelectedStudentId('');
    setStudentSearchTerm('');
    setGeneratedDocument(null);
    setSavedDocumentId('');
    setGenerationError('');
    setSaveError('');
  };

  const resetDraft = () => {
    setForm(createDefaultFormState());
    setReferenceNumber(buildReferenceNumber());
    setStudentSearchTerm('');
    setSelectedStudentId('');
    setPromptDraft('');
    setPromptHistory([]);
    setGeneratedDocument(null);
    setSavedDocumentId('');
    setGenerationError('');
    setSaveError('');
  };

  const regenerateReference = () => {
    setReferenceNumber(buildReferenceNumber());
  };

  const buildPromptConversation = (history = []) => history.flatMap((entry) => {
    const messages = [];

    if (entry.prompt) {
      messages.push({
        role: 'user',
        content: entry.prompt
      });
    }

    if (entry.resultSummary) {
      messages.push({
        role: 'assistant',
        content: entry.resultSummary
      });
    }

    return messages;
  });

  const summarizeGeneratedDocument = (document) => {
    const subject = normalizeText(document?.subject || document?.documentLabel || previewData.subject);
    const openingCount = Array.isArray(document?.openingParagraphs) ? document.openingParagraphs.length : 0;
    const bodyCount = Array.isArray(document?.bodyParagraphs) ? document.bodyParagraphs.length : 0;
    const bulletCount = Array.isArray(document?.bulletItems) ? document.bulletItems.length : 0;
    const source = normalizeText(document?.source) || 'backend';

    return `Generated ${subject} with ${openingCount} opening paragraph${openingCount === 1 ? '' : 's'}, ${bodyCount} body paragraph${bodyCount === 1 ? '' : 's'}, and ${bulletCount} bullet item${bulletCount === 1 ? '' : 's'} from ${source}.`;
  };

  const generateLivePreview = async () => {
    const promptValue = normalizeText(promptDraft) || promptText;
    const requestBody = {
      ...form,
      referenceNumber,
      generatedDate,
      studentKey: selectedStudent?.studentKey || selectedStudent?.id || '',
      prompt: promptValue,
      promptHistory: buildPromptConversation(promptHistory),
      persistDocument: false
    };

    setGeneratingDocument(true);
    setGenerationError('');
    setSaveError('');

    try {
      const response = await fetch(`${DOCUMENT_GENERATION_API_BASE}/document-generation/generate?format=json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(DOCUMENT_GENERATION_SHARED_SECRET
            ? { 'x-document-generation-secret': DOCUMENT_GENERATION_SHARED_SECRET }
            : {})
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to generate document');
      }

      if (data.referenceNumber) {
        setReferenceNumber(data.referenceNumber);
      }

      if (data.generatedDate) {
        setForm((current) => ({
          ...current,
          meetingDate: current.meetingDate || data.generatedDate
        }));
      }

      if (data.document) {
        setGeneratedDocument(data.document);
      }

      setSavedDocumentId('');

      const resultSummary = summarizeGeneratedDocument(data.document);
      setPromptHistory((current) => [
        ...current,
        {
          prompt: promptValue,
          resultSummary,
          createdAt: new Date().toISOString()
        }
      ]);
    } catch (error) {
      setGenerationError(error?.message || 'Failed to generate document preview');
    } finally {
      setGeneratingDocument(false);
    }
  };

  const saveCurrentDocument = async () => {
    if (!generatedDocument) {
      setSaveError('Generate a document first before saving it.');
      return;
    }

    setSavingDocument(true);
    setSaveError('');

    try {
      const html = await buildExportHtml();
      const response = await fetch(`${DOCUMENT_GENERATION_API_BASE}/documents/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(DOCUMENT_GENERATION_SHARED_SECRET
            ? { 'x-document-generation-secret': DOCUMENT_GENERATION_SHARED_SECRET }
            : {})
        },
        body: JSON.stringify({
          payload: {
            ...form,
            referenceNumber,
            generatedDate,
            studentKey: selectedStudent?.studentKey || selectedStudent?.id || '',
            prompt: normalizeText(promptDraft) || promptText,
            promptHistory: buildPromptConversation(promptHistory),
            persistDocument: true
          },
          document: generatedDocument,
          html,
          selectedStudent: selectedStudent || null
        })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to save document');
      }

      if (data.savedDocumentId) {
        setSavedDocumentId(data.savedDocumentId);
      }
    } catch (error) {
      setSaveError(error?.message || 'Failed to save document');
    } finally {
      setSavingDocument(false);
    }
  };

  const buildExportHtml = async () => {
    let logoDataUrl = '';
    let signatureDataUrl = '';

    try {
      logoDataUrl = await loadImageAsDataUrl(logo);
    } catch (error) {
      console.warn('Could not embed logo in Word export:', error);
    }

    try {
      signatureDataUrl = await loadImageAsDataUrl(signature);
    } catch (error) {
      console.warn('Could not embed signature image in Word export:', error);
    }

    const headerHtml = form.includeLetterhead
      ? `
        <div class="doc-word-letterhead">
          <div class="doc-word-logoWrap">
            ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Ubunifu College" class="doc-word-logo" />` : ''}
          </div>
          <div class="doc-word-contact">
            ${collegeContactLines.map((line) => `<div class="doc-word-contactLine">${escapeHtml(line)}</div>`).join('')}
          </div>
        </div>
      `
      : '';

    const referenceHtml = '';

    const openingHtml = previewData.openingParagraphs
      .map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
      .join('');

    const bodyHtml = previewData.bodyParagraphs
      .map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
      .join('');

    const bulletHtml = previewData.bulletItems.length
      ? `
        ${previewData.bulletHeading ? `<p><strong>${escapeHtml(previewData.bulletHeading)}</strong></p>` : ''}
        <ul>
          ${previewData.bulletItems.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}
        </ul>
      `
      : '';

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(previewData.subject)}</title>
          <style>
            body {
              margin: 0;
              padding: 40px 48px;
              font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              font-size: 12px;
              color: #1d1d1d;
              background: #ffffff;
              line-height: 1.7;
              -webkit-font-smoothing: antialiased;
              text-rendering: optimizeLegibility;
            }
            .doc-word-sheet {
              max-width: 780px;
              margin: 0 auto;
              min-height: 1040px;
              display: flex;
              flex-direction: column;
            }
            .doc-word-letterhead {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 24px;
              margin-bottom: 22px;
              padding-bottom: 18px;
              border-bottom: 1px solid #d1d5db;
            }
            .doc-word-logoWrap {
              flex: 0 0 auto;
            }
            .doc-word-logo {
              width: 160px;
              height: auto;
              max-height: 160px;
              object-fit: contain;
            }
            .doc-word-contact {
              margin-left: auto;
              text-align: right;
              font-size: 12px;
              line-height: 1.55;
              color: #1d1d1d;
            }
            .doc-word-contactLine {
              margin-bottom: 1px;
            }
            .doc-word-contactLine--brand {
              font-weight: 700;
            }
            .doc-word-referenceInline {
              font-size: 11px;
              letter-spacing: 0.08em;
              text-transform: uppercase;
              color: #6d6d6d;
              margin: 0 0 18px;
            }
            .doc-word-reference {
              text-align: right;
              font-size: 11px;
              letter-spacing: 0.08em;
              text-transform: uppercase;
              color: #6d6d6d;
              margin-bottom: 18px;
            }
            h1 {
              margin: 0 0 6px;
              font-size: 22px;
            }
            .doc-word-meta {
              font-size: 12px;
              color: #5d5d5d;
              margin-bottom: 24px;
            }
            .doc-word-metaSimple {
              display: grid;
              gap: 4px;
              font-size: 12px;
              color: #5d5d5d;
              margin-bottom: 24px;
            }
            .doc-word-metaSimple span:last-child {
              font-weight: 700;
            }
            .doc-word-body {
              flex: 1;
              display: flex;
              flex-direction: column;
            }
            .doc-word-signatureImage {
              width: 180px;
              max-width: 100%;
              height: auto;
              object-fit: contain;
              margin: 0 0 2px;
            }
            p {
              margin: 0 0 14px;
            }
            ul {
              margin: 0 0 14px 22px;
            }
          </style>
        </head>
        <body>
          <div class="doc-word-sheet">
            ${headerHtml}
            ${referenceHtml}
            <div class="doc-word-metaSimple">
              <span>Date: ${escapeHtml(generatedDate)}</span>
              ${form.includeReference ? `<span>Reference: ${escapeHtml(referenceNumber)}</span>` : ''}
            </div>
            <div class="doc-word-body">
              ${openingHtml}
              ${bodyHtml}
              ${bulletHtml}
              ${signatureDataUrl ? `<img src="${signatureDataUrl}" alt="Signature" class="doc-word-signatureImage" />` : ''}
              <p><strong>${escapeHtml(previewData.closingLabel)}</strong></p>
              <p><strong>${escapeHtml(form.signatoryName)}</strong><br />${escapeHtml(form.signatoryRole)}</p>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const downloadPdf = async () => {
    if (!previewRef.current) return;

    setExporting('pdf');

    try {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      const images = previewRef.current.querySelectorAll('img');
      await Promise.all(Array.from(images).map((img) => (
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            })
      )));

      await html2pdf()
        .set({
          margin: 8,
          filename: `${sanitizeFileName(previewData.subject)}_${sanitizeFileName(previewData.studentName)}.pdf`,
          image: { type: 'png' },
          html2canvas: { scale: 3, useCORS: true, backgroundColor: '#ffffff', letterRendering: true, scrollY: 0 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        })
        .from(previewRef.current)
        .save();
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setExporting('');
    }
  };

  const downloadWord = async () => {
    setExporting('word');

    try {
      const html = await buildExportHtml();
      const blob = new Blob([html], { type: 'application/msword;charset=utf-8' });
      saveAs(blob, `${sanitizeFileName(previewData.subject)}_${sanitizeFileName(previewData.studentName)}.doc`);
    } catch (error) {
      console.error('Failed to export Word document:', error);
    } finally {
      setExporting('');
    }
  };

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <div className="clientsComp applicationsPage businessInfoPage documentGeneratorPage">
          <div className="documentGeneratorHeader appsHeader">
            <div>
              <p className="appsKicker">Business Info</p>
              <h4>Document Generator</h4>
              <p className="appsSubtext">
                Draft recommendation letters, confirmations, and custom business documents with live preview and export controls.
              </p>
            </div>
          </div>

          <div className="documentGeneratorToolbar">
            <Button variant="outline-dark" onClick={() => setSetupModalOpen(true)}>
              <FontAwesomeIcon icon={faWandSparkles} className="me-2" />
              Document setup
            </Button>
            <Button variant="outline-dark" onClick={() => setContentModalOpen(true)}>
              <FontAwesomeIcon icon={faPenNib} className="me-2" />
              Letter content
            </Button>
            <Button
              variant="outline-dark"
              onClick={() => setPromptModalOpen(true)}
            >
              <FontAwesomeIcon icon={faFileLines} className="me-2" />
              Document generation prompt
            </Button>
            <Button variant="outline-dark" onClick={resetDraft}>
              <FontAwesomeIcon icon={faRotateRight} className="me-2" />
              Reset draft
            </Button>
          </div>

          <div className="documentGeneratorGrid">
            <div className="documentGeneratorPreviewColumn">
              <div className="documentGeneratorCard documentGeneratorPreviewCard">
                <div className="documentGeneratorSectionHeader documentGeneratorSectionHeader--preview">
                  <div>
                    <p className="appsKicker">Live preview</p>
                    <h6>{previewData.subject}</h6>
                    <p className="documentGeneratorSectionSubtext mb-0">
                      {documentIsSaved
                        ? 'Saved to Firestore and ready for download.'
                        : generatedDocument
                          ? 'Review the draft, then save it to unlock downloads.'
                          : 'This layout updates instantly as you edit the form.'}
                    </p>
                  </div>
                  <div className="documentGeneratorPreviewActions">
                    {documentIsSaved ? (
                      <span className="documentGeneratorReadyBadge">
                        <FontAwesomeIcon icon={faWandSparkles} className="me-1" />
                        Saved
                      </span>
                    ) : (
                      <Button
                        variant="outline-dark"
                        onClick={saveCurrentDocument}
                        disabled={savingDocument || !generatedDocument}
                      >
                        <FontAwesomeIcon icon={faWandSparkles} className="me-2" />
                        {savingDocument ? 'Saving...' : 'Save document'}
                      </Button>
                    )}
                    {documentIsSaved && (
                      <>
                        <Button
                          variant="outline-dark"
                          onClick={downloadPdf}
                          disabled={exporting === 'pdf'}
                        >
                          <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                          {exporting === 'pdf' ? 'Preparing PDF...' : 'Download PDF'}
                        </Button>
                        <Button
                          variant="outline-dark"
                          onClick={downloadWord}
                          disabled={exporting === 'word'}
                        >
                          <FontAwesomeIcon icon={faFileWord} className="me-2" />
                          {exporting === 'word' ? 'Preparing Word...' : 'Download Word'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <div className="documentGeneratorPaperShell">
                  <div className="documentGeneratorPaper" ref={previewRef}>
                    {form.includeLetterhead && (
                      <div className="documentGeneratorPaperHeader">
                        <div className="documentGeneratorLogoWrap">
                          <img src={logo} alt="Ubunifu College" className="documentGeneratorLogo" />
                        </div>
                        <div className="documentGeneratorContactBlock">
                          {collegeContactLines.map((line, index) => (
                            <div
                              key={line}
                              className={`documentGeneratorContactLine${index === 0 ? ' documentGeneratorContactLine--brand' : ''}`}
                            >
                              {line}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="documentGeneratorDocTitle">
                      <div className="documentGeneratorDocMeta documentGeneratorDocMeta--simple">
                        <span>Date: {generatedDate}</span>
                        {form.includeReference && <span>Reference: {referenceNumber}</span>}
                      </div>
                    </div>
                    {(saveError || generationError) && (
                      <div className="documentGeneratorPromptError mb-3">
                        {saveError || generationError}
                      </div>
                    )}

                    <div className="documentGeneratorBody">
                      <div className="documentGeneratorBodyContent">
                        {previewData.openingParagraphs.map((paragraph, index) => (
                          <p key={`opening-${index}-${paragraph}`}>{paragraph}</p>
                        ))}

                        {previewData.bodyParagraphs.map((paragraph, index) => (
                          <p key={`${index}-${paragraph}`}>{paragraph}</p>
                        ))}

                        {previewData.bulletItems.length > 0 && (
                          <div>
                            {previewData.bulletHeading && <p className="mb-2"><strong>{previewData.bulletHeading}</strong></p>}
                            <ul className="documentGeneratorBullets">
                              {previewData.bulletItems.map((item, index) => (
                                <li key={`${index}-${item}`}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <img src={signature} alt="Signature" className="documentGeneratorSignatureImage" />
                        <p><strong>{previewData.closingLabel}</strong></p>
                        <div className="documentGeneratorSignature">
                          <strong>{form.signatoryName}</strong>
                          <span>{form.signatoryRole}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <Modal show={setupModalOpen} onHide={() => setSetupModalOpen(false)} centered size="lg">
              <Modal.Header closeButton>
                <Modal.Title>Document setup</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="documentGeneratorSectionHeader documentGeneratorSectionHeader--modal">
                  <div>
                    <p className="appsKicker">Options and output controls</p>
                    <h6 className="mb-0">Turn sections on or off before the backend is connected.</h6>
                  </div>
                </div>

                <div className="documentGeneratorSwitchGrid">
                  <div className="documentGeneratorSwitchRow">
                    <div>
                      <h6 className="mb-1">Letterhead</h6>
                      <p className="mb-0">Show the school branding at the top of the page.</p>
                    </div>
                    <Form.Check
                      type="switch"
                      id="include-letterhead"
                      checked={form.includeLetterhead}
                      onChange={updateField('includeLetterhead')}
                    />
                  </div>

                  <div className="documentGeneratorSwitchRow">
                    <div>
                      <h6 className="mb-1">Document reference</h6>
                      <p className="mb-0">Display a unique reference number in the final document.</p>
                    </div>
                    <Form.Check
                      type="switch"
                      id="include-reference"
                      checked={form.includeReference}
                      onChange={updateField('includeReference')}
                    />
                  </div>
                </div>

                <div className="documentGeneratorFieldGrid documentGeneratorFieldGrid--3 mt-4">
                  <Form.Group>
                    <Form.Label>Document type</Form.Label>
                    <Form.Select value={form.documentType} onChange={updateField('documentType')}>
                      {DOCUMENT_TYPES.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Tone</Form.Label>
                    <Form.Select value={form.tone} onChange={updateField('tone')}>
                      {TONE_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Reference number</Form.Label>
                    <Form.Control type="text" value={referenceNumber} readOnly />
                  </Form.Group>
                </div>

                <div className="d-flex flex-wrap gap-2 mt-3">
                  <Button variant="outline-dark" onClick={regenerateReference}>
                    Regenerate reference
                  </Button>
                  <div className="documentGeneratorInlineMeta">
                    {form.includeReference ? `Will appear on the document as ${referenceNumber}` : 'Hidden from the final export'}
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setSetupModalOpen(false)}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            <Modal show={promptModalOpen} onHide={() => setPromptModalOpen(false)} centered size="lg">
              <Modal.Header closeButton>
                <Modal.Title>Document Generation Prompt</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="documentGeneratorSectionHeader documentGeneratorSectionHeader--modal">
                  <div>
                    <p className="appsKicker">OpenAI brief</p>
                    <h6 className="mb-0">Structured prompt for backend generation.</h6>
                  </div>
                </div>

                <div className="documentGeneratorPromptBox documentGeneratorPromptBox--modal">
                  <Form.Group className="mb-0">
                    <Form.Label className="documentGeneratorPromptLabel">Editable prompt</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={18}
                      value={promptDraft}
                      onChange={(event) => setPromptDraft(event.target.value)}
                      className="documentGeneratorPromptEditor"
                    />
                  </Form.Group>
                </div>

                <div className="documentGeneratorPromptHistory mt-3">
                  <div className="documentGeneratorPromptHistoryHeader">
                    <strong>Prompt context</strong>
                    <span>{promptHistory.length} saved revision{promptHistory.length === 1 ? '' : 's'}</span>
                  </div>

                  {promptHistory.length > 0 ? (
                    <div className="documentGeneratorPromptHistoryList">
                      {promptHistory.slice().reverse().map((entry, index) => (
                        <div key={`${entry.createdAt || 'history'}-${index}`} className="documentGeneratorPromptHistoryItem">
                          <div className="documentGeneratorPromptHistoryItemTop">
                            <span className="documentGeneratorPromptHistoryLabel">Revision {promptHistory.length - index}</span>
                            {entry.createdAt && <span>{new Date(entry.createdAt).toLocaleString()}</span>}
                          </div>
                          <div className="documentGeneratorPromptHistoryText">
                            {entry.prompt}
                          </div>
                          <div className="documentGeneratorPromptHistorySummary">
                            {entry.resultSummary}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="documentGeneratorInlineMeta">
                      No generation history yet. Your first run will appear here so you can continue from the same context.
                    </div>
                  )}
                </div>

                {generationError && (
                  <div className="documentGeneratorPromptError mt-3">
                    {generationError}
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="outline-dark"
                  onClick={generateLivePreview}
                  disabled={generatingDocument}
                >
                  <FontAwesomeIcon icon={faWandSparkles} className="me-2" />
                  {generatingDocument ? 'Generating preview...' : 'Generate preview'}
                </Button>
                <Button variant="secondary" onClick={() => setPromptModalOpen(false)}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>

            <Modal show={contentModalOpen} onHide={() => setContentModalOpen(false)} centered size="lg">
              <Modal.Header closeButton>
                <Modal.Title>Letter content</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="documentGeneratorSectionHeader documentGeneratorSectionHeader--modal">
                  <div>
                    <p className="appsKicker">What the document should say</p>
                    <h6 className="mb-0">Fill in the content the AI should later refine.</h6>
                  </div>
                </div>

                {isStudentDocument ? (
                  <div className="documentGeneratorStudentSearch">
                    <Form.Group>
                      <Form.Label>Search student from system</Form.Label>
                      <Form.Control
                        type="search"
                        value={studentSearchTerm}
                        onChange={handleStudentSearchChange}
                        placeholder="Type a student name, admission number, or course"
                      />
                    </Form.Group>

                    {selectedStudent && (
                      <div className="documentGeneratorStudentSelected">
                        <div className="documentGeneratorStudentSelectedTop">
                          <strong>{selectedStudent.displayName}</strong>
                          <Button variant="link" className="documentGeneratorStudentClearButton" onClick={clearStudentSelection}>
                            Clear
                          </Button>
                        </div>
                        <div className="documentGeneratorStudentSelectedMeta">
                          <span>{selectedStudent.admissionNumber || 'Admission number not set'}</span>
                          <span>{selectedStudent.course || 'Course not set'}</span>
                          {selectedStudent.cohort && <span>{selectedStudent.cohort}</span>}
                        </div>
                      </div>
                    )}

                    {!selectedStudent && studentSearchTerm.trim() && (
                      <div className="documentGeneratorStudentResults">
                        {studentMatches.length > 0 ? (
                          studentMatches.map((student) => (
                            <button
                              key={student.studentKey}
                              type="button"
                              className="documentGeneratorStudentResultButton"
                              onClick={() => selectStudent(student)}
                            >
                              <span className="documentGeneratorStudentResultName">{student.displayName}</span>
                              <span className="documentGeneratorStudentResultMeta">
                                {student.admissionNumber || 'Admission number not set'}
                                {student.course ? ` • ${student.course}` : ''}
                                {student.cohort ? ` • ${student.cohort}` : ''}
                              </span>
                            </button>
                          ))
                        ) : (
                          <div className="documentGeneratorStudentEmpty">
                            No matching student found in the system.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="documentGeneratorStudentEmpty mb-3">
                    This document type does not use a student reference.
                  </div>
                )}

                {isStudentDocument && (
                  <>
                    <div className="documentGeneratorFieldGrid">
                      <Form.Group>
                        <Form.Label>Student name</Form.Label>
                        <Form.Control type="text" value={form.studentName} onChange={updateField('studentName')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Admission number</Form.Label>
                        <Form.Control type="text" value={form.admissionNumber} onChange={updateField('admissionNumber')} />
                      </Form.Group>
                    </div>

                    <div className="documentGeneratorFieldGrid mt-3">
                      <Form.Group>
                        <Form.Label>Course / program</Form.Label>
                        <Form.Control type="text" value={form.course} onChange={updateField('course')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Cohort / class</Form.Label>
                        <Form.Control type="text" value={form.cohort} onChange={updateField('cohort')} />
                      </Form.Group>
                    </div>

                    <div className="documentGeneratorFieldGrid mt-3">
                      <Form.Group>
                        <Form.Label>Requested outcome</Form.Label>
                        <Form.Control type="text" value={form.requestedOutcome} onChange={updateField('requestedOutcome')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Relationship context</Form.Label>
                        <Form.Control type="text" value={form.relationshipContext} onChange={updateField('relationshipContext')} />
                      </Form.Group>
                    </div>

                    <div className="documentGeneratorFieldGrid mt-3">
                      <Form.Group>
                        <Form.Label>Strength one</Form.Label>
                        <Form.Control type="text" value={form.strengthOne} onChange={updateField('strengthOne')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Strength two</Form.Label>
                        <Form.Control type="text" value={form.strengthTwo} onChange={updateField('strengthTwo')} />
                      </Form.Group>
                    </div>

                    <div className="documentGeneratorFieldGrid mt-3">
                      <Form.Group>
                        <Form.Label>Strength three</Form.Label>
                        <Form.Control type="text" value={form.strengthThree} onChange={updateField('strengthThree')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Business notes</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={form.extraNotes}
                          onChange={updateField('extraNotes')}
                          placeholder="Add any custom details the backend should later refine."
                        />
                      </Form.Group>
                    </div>
                  </>
                )}

                {isBoardResolutionDocument && (
                  <>
                    <div className="documentGeneratorFieldGrid">
                      <Form.Group>
                        <Form.Label>Meeting title</Form.Label>
                        <Form.Control type="text" value={form.meetingTitle} onChange={updateField('meetingTitle')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Meeting date</Form.Label>
                        <Form.Control type="text" value={form.meetingDate} onChange={updateField('meetingDate')} />
                      </Form.Group>
                    </div>

                    <div className="documentGeneratorFieldGrid mt-3">
                      <Form.Group>
                        <Form.Label>Meeting location</Form.Label>
                        <Form.Control type="text" value={form.meetingLocation} onChange={updateField('meetingLocation')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Attendance</Form.Label>
                        <Form.Control type="text" value={form.meetingAttendees} onChange={updateField('meetingAttendees')} />
                      </Form.Group>
                    </div>

                    <div className="documentGeneratorFieldGrid mt-3">
                      <Form.Group>
                        <Form.Label>Resolution summary</Form.Label>
                        <Form.Control type="text" value={form.boardResolutionSummary} onChange={updateField('boardResolutionSummary')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Resolution points</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={form.boardResolutionPoints}
                          onChange={updateField('boardResolutionPoints')}
                          placeholder="List the resolution points, one per line."
                        />
                      </Form.Group>
                    </div>
                  </>
                )}

                {isMinutesDocument && (
                  <>
                    <div className="documentGeneratorFieldGrid">
                      <Form.Group>
                        <Form.Label>Meeting title</Form.Label>
                        <Form.Control type="text" value={form.meetingTitle} onChange={updateField('meetingTitle')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Meeting date</Form.Label>
                        <Form.Control type="text" value={form.meetingDate} onChange={updateField('meetingDate')} />
                      </Form.Group>
                    </div>

                    <div className="documentGeneratorFieldGrid mt-3">
                      <Form.Group>
                        <Form.Label>Meeting location</Form.Label>
                        <Form.Control type="text" value={form.meetingLocation} onChange={updateField('meetingLocation')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Attendance</Form.Label>
                        <Form.Control type="text" value={form.meetingAttendees} onChange={updateField('meetingAttendees')} />
                      </Form.Group>
                    </div>

                    <div className="documentGeneratorFieldGrid mt-3">
                      <Form.Group>
                        <Form.Label>Minutes summary</Form.Label>
                        <Form.Control type="text" value={form.minutesSummary} onChange={updateField('minutesSummary')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Minutes items</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={form.minutesItems}
                          onChange={updateField('minutesItems')}
                          placeholder="List the minute items or action points, one per line."
                        />
                      </Form.Group>
                    </div>
                  </>
                )}

                {isCustomDocument && (
                  <>
                    <div className="documentGeneratorFieldGrid">
                      <Form.Group>
                        <Form.Label>Business subject</Form.Label>
                        <Form.Control type="text" value={form.businessSubject} onChange={updateField('businessSubject')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Business purpose</Form.Label>
                        <Form.Control type="text" value={form.requestedOutcome} onChange={updateField('requestedOutcome')} />
                      </Form.Group>
                    </div>

                    <div className="documentGeneratorFieldGrid mt-3">
                      <Form.Group>
                        <Form.Label>Context</Form.Label>
                        <Form.Control type="text" value={form.relationshipContext} onChange={updateField('relationshipContext')} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Business summary</Form.Label>
                        <Form.Control type="text" value={form.businessSummary} onChange={updateField('businessSummary')} />
                      </Form.Group>
                    </div>
                  </>
                )}

                <div className="documentGeneratorFieldGrid mt-3">
                  <Form.Group>
                    <Form.Label>Recipient name</Form.Label>
                    <Form.Control type="text" value={form.recipientName} onChange={updateField('recipientName')} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Recipient title</Form.Label>
                    <Form.Control type="text" value={form.recipientTitle} onChange={updateField('recipientTitle')} />
                  </Form.Group>
                </div>

                <div className="mt-3">
                  <Form.Group>
                    <Form.Label>Extra notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={form.extraNotes}
                      onChange={updateField('extraNotes')}
                      placeholder="Add any custom details the backend should later refine."
                    />
                  </Form.Group>
                </div>

                <div className="documentGeneratorFieldGrid mt-3">
                  <Form.Group>
                    <Form.Label>Signatory name</Form.Label>
                    <Form.Control type="text" value={form.signatoryName} onChange={updateField('signatoryName')} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Signatory role</Form.Label>
                    <Form.Control type="text" value={form.signatoryRole} onChange={updateField('signatoryRole')} />
                  </Form.Group>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setContentModalOpen(false)}>
                  Close
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentGenerator;
